import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Package, Wrench } from "lucide-react";

/**
 * Records the Parts & Services USED on a job card (no prices here).
 * Prices are entered later on the invoice. Items are stored in the job card's
 * `parts_used` array (JobCardItem shape). For 'part' items we capture part_id +
 * brand so the invoice can auto-fill the selling price and deduct the right stock.
 *
 * Props:
 *   items: array of current used items
 *   onItemsChange: (nextItems) => void
 *   vehicleMake / vehicleModel: current job card's vehicle, used to auto-filter
 *   the "Pick from Inventory" list to compatible parts first
 */
const JobCardUsedItems = ({ items = [], onItemsChange, vehicleMake, vehicleModel }) => {
  const [parts, setParts] = useState([]);
  const [services, setServices] = useState([]);
  const [showAllParts, setShowAllParts] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [current, setCurrent] = useState({
    item_type: "part",
    catalog_id: "",
    description: "",
    part_number: "",
    quantity: 1,
    unit: "pcs",
  });

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "engine", label: "Engine" },
    { value: "transmission", label: "Transmission" },
    { value: "brakes", label: "Brakes" },
    { value: "suspension", label: "Suspension" },
    { value: "electrical", label: "Electrical" },
    { value: "body", label: "Body" },
    { value: "interior", label: "Interior" },
    { value: "exhaust", label: "Exhaust" },
    { value: "cooling", label: "Cooling" },
    { value: "fuel_system", label: "Fuel System" },
    { value: "ac_system", label: "AC System" },
    { value: "filters", label: "Filters" },
    { value: "oils_fluids", label: "Oils & Fluids" },
    { value: "tires", label: "Tires" },
    { value: "others", label: "Others" },
  ];

  const isCompatiblePart = (p) => {
    if (!vehicleMake) return true;
    const makes = (p.compatible_makes || []).map((m) => (m || "").toLowerCase().trim());
    if (makes.length === 0) return true; // universal part, fits any vehicle
    if (!makes.includes(vehicleMake.toLowerCase().trim())) return false;
    const models = (p.compatible_models || []).map((m) => (m || "").toLowerCase().trim());
    if (models.length === 0 || !vehicleModel) return true;
    return models.includes(vehicleModel.toLowerCase().trim());
  };

  const matchesCategory = (p) => categoryFilter === "all" || p.category === categoryFilter;

  const categoryFilteredParts = parts.filter(matchesCategory);
  const compatibleParts = vehicleMake ? categoryFilteredParts.filter(isCompatiblePart) : categoryFilteredParts;
  const otherParts = vehicleMake ? categoryFilteredParts.filter((p) => !isCompatiblePart(p)) : [];
  const visibleParts = showAllParts || !vehicleMake ? categoryFilteredParts : compatibleParts;

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          axios.get(`${API}/parts`),
          axios.get(`${API}/services`),
        ]);
        setParts(Array.isArray(pRes.data) ? pRes.data : []);
        setServices(Array.isArray(sRes.data) ? sRes.data : []);
      } catch (e) {
        setParts([]);
        setServices([]);
      }
    };
    load();
  }, []);

  const resetCurrent = (item_type = "part") =>
    setCurrent({
      item_type,
      catalog_id: "",
      description: "",
      part_number: "",
      quantity: 1,
      unit: item_type === "labor" ? "hours" : "pcs",
    });

  // When a catalog part is picked, prefill description + part number
  const handlePartPick = (partId) => {
    const p = parts.find((x) => x.id === partId);
    setCurrent((c) => ({
      ...c,
      catalog_id: partId,
      description: p ? p.part_name || p.name || "" : c.description,
      part_number: p ? p.part_number || "" : c.part_number,
    }));
  };

  const handleServicePick = (serviceId) => {
    const s = services.find((x) => x.id === serviceId);
    setCurrent((c) => ({
      ...c,
      catalog_id: serviceId,
      description: s ? s.name || "" : c.description,
    }));
  };

  const addItem = () => {
    const desc = (current.description || "").trim();
    if (!desc) return;

    let part_id = null;
    let brand_id = null;
    let brand_name = null;
    if (current.item_type === "part" && current.catalog_id) {
      const p = parts.find((x) => x.id === current.catalog_id);
      if (p) {
        part_id = p.id;
        const firstBrand = (p.brands || []).find((b) => b.is_active) || (p.brands || [])[0];
        if (firstBrand) {
          brand_id = firstBrand.id || null;
          brand_name = firstBrand.brand_name || null;
        }
      }
    }

    const newItem = {
      id: Date.now().toString(),
      item_type: current.item_type,
      description: desc,
      name: desc,
      quantity: parseFloat(current.quantity) || 1,
      unit: current.unit,
      part_number: (current.part_number || "").trim() || null,
      part_id,
      brand_id,
      brand_name,
    };

    onItemsChange([...(items || []), newItem]);
    resetCurrent(current.item_type);
  };

  const removeItem = (id) => {
    onItemsChange((items || []).filter((it) => it.id !== id));
  };

  const typeLabel = (t) => (t === "labor" ? "Service" : t === "part" ? "Part" : t);

  return (
    <div className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm" data-testid="jobcard-used-items">
      <div className="flex items-center mb-1">
        <Package className="h-5 w-5 mr-2 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-800">Parts &amp; Services Used</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Record what was used on this job. No prices needed here — they are added on the invoice.
      </p>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Add Item</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Select
                value={current.item_type}
                onValueChange={(v) => resetCurrent(v)}
              >
                <SelectTrigger data-testid="used-item-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="part">Spare Part</SelectItem>
                  <SelectItem value="labor">Service / Labour</SelectItem>
                  <SelectItem value="consumable">Consumable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {current.item_type === "part" && parts.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="mb-0">Category</Label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid="used-item-category-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Pick from Inventory (optional)</Label>
                  {vehicleMake && otherParts.length > 0 && (
                    <button
                      type="button"
                      data-testid="toggle-show-all-parts"
                      onClick={() => setShowAllParts((v) => !v)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {showAllParts ? "Show compatible only" : "Show all parts"}
                    </button>
                  )}
                </div>
                {vehicleMake && (
                  <p className="text-xs text-gray-500 mb-1" data-testid="parts-filter-hint">
                    {showAllParts
                      ? `Showing all parts (${categoryFilteredParts.length})`
                      : `Showing parts for ${vehicleMake}${vehicleModel ? " " + vehicleModel : ""} (${compatibleParts.length})`}
                  </p>
                )}
                <Select value={current.catalog_id || undefined} onValueChange={handlePartPick}>
                  <SelectTrigger data-testid="used-item-part-select">
                    <SelectValue placeholder="Select a part" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {!showAllParts && vehicleMake ? (
                      <>
                        {compatibleParts.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.part_name} {p.part_number ? `(${p.part_number})` : ""}
                          </SelectItem>
                        ))}
                        {compatibleParts.length === 0 && (
                          <div className="px-2 py-1.5 text-xs text-gray-500">
                            No compatible parts found — try "Show all parts" or a different Category
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {visibleParts.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.part_name} {p.part_number ? `(${p.part_number})` : ""}
                          </SelectItem>
                        ))}
                        {visibleParts.length === 0 && (
                          <div className="px-2 py-1.5 text-xs text-gray-500">
                            No parts found in this category
                          </div>
                        )}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {current.item_type === "labor" && services.length > 0 && (
              <div>
                <Label>Pick a Service (optional)</Label>
                <Select value={current.catalog_id || undefined} onValueChange={handleServicePick}>
                  <SelectTrigger data-testid="used-item-service-select">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label>Description *</Label>
              <Input
                data-testid="used-item-description"
                value={current.description}
                onChange={(e) => setCurrent({ ...current, description: e.target.value })}
                placeholder="e.g., Engine Oil 5W-30 / Brake service"
              />
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                data-testid="used-item-quantity"
                type="number"
                min="0"
                step="0.1"
                value={current.quantity}
                onChange={(e) => setCurrent({ ...current, quantity: e.target.value })}
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={current.unit} onValueChange={(v) => setCurrent({ ...current, unit: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pcs">Pieces</SelectItem>
                  <SelectItem value="liters">Liters</SelectItem>
                  <SelectItem value="kg">Kilograms</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="set">Set</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {current.item_type === "part" && (
            <div className="md:w-1/3">
              <Label>Part Number (optional)</Label>
              <Input
                data-testid="used-item-part-number"
                value={current.part_number}
                onChange={(e) => setCurrent({ ...current, part_number: e.target.value })}
                placeholder="Optional part number"
              />
            </div>
          )}

          <Button
            type="button"
            onClick={addItem}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="used-item-add-btn"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Item
          </Button>
        </CardContent>
      </Card>

      {(items || []).length > 0 && (
        <Card className="mt-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Items Used ({items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="used-items-list">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center p-3 border rounded-lg"
                  data-testid={`used-item-row-${item.id}`}
                >
                  <div className="flex items-center gap-2">
                    {item.item_type === "labor" ? (
                      <Wrench className="h-4 w-4 text-purple-600" />
                    ) : (
                      <Package className="h-4 w-4 text-blue-600" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {typeLabel(item.item_type)}
                        </Badge>
                        <span className="font-medium">{item.description}</span>
                      </div>
                      <div className="text-xs text-gray-600">
                        {item.quantity} {item.unit}
                        {item.part_number ? ` • Part: ${item.part_number}` : ""}
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => removeItem(item.id)}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    data-testid={`used-item-remove-${item.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JobCardUsedItems;
